import { supabase } from '@/integrations/supabase/client';

export interface WebsiteAnalysisData {
  website_content: any[];
  website_info: any;
  key_content_pages: any[];
  post_ideas: any[];
  language: string;
  offering: string;
  goal: string;
}

export interface ChatMessage {
  role: 'assistant' | 'user';
  content: string;
  _id?: number;
  _streaming?: boolean;
  _typing?: boolean;
  _magic?: string;
  _loading?: boolean;
  _mode?: 'unknown' | 'chat' | 'idea' | 'write';
}

export interface ChatSendOptions {
  input: string;
  messages: ChatMessage[];
  currentWebsite?: { id: string; url: string };
  websiteAnalysisData: WebsiteAnalysisData;
  setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
  setSending: (sending: boolean) => void;
}

export const gatherWebsiteAnalysisData = (): WebsiteAnalysisData => {
  return {
    website_content: JSON.parse(localStorage.getItem('website_content') || '[]'),
    website_info: JSON.parse(localStorage.getItem('website_info') || '{}'),
    key_content_pages: JSON.parse(localStorage.getItem('key_content_pages') || '[]'),
    post_ideas: JSON.parse(localStorage.getItem('post_ideas') || '[]'),
    language: localStorage.getItem('website_language') || localStorage.getItem('language') || 'en',
    offering: localStorage.getItem('website_offering') || '',
    goal: localStorage.getItem('website_goal') || ''
  };
};

export const sendChatMessage = async (options: ChatSendOptions): Promise<void> => {
  const { input, messages, currentWebsite, websiteAnalysisData, setMessages, setSending } = options;
  
  if (!input.trim()) return;
  const text = input.trim();
  setMessages((m) => [...m, { role: 'user', content: text }]);
  setSending(true);

  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  try {
    const { data: sessionData } = await supabase.auth.getSession();
    
    // Add streaming assistant message placeholder
    const assistantMessageId = Date.now();
    setMessages((m) => [...m, { 
      role: 'assistant', 
      content: '', 
      _id: assistantMessageId, 
      _streaming: true, 
      _loading: true, 
      _mode: 'unknown' 
    }]);

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-onboarding`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionData?.session?.access_token ? { 'Authorization': `Bearer ${sessionData.session.access_token}` } : {}),
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
      },
      body: JSON.stringify({
        website_id: currentWebsite?.id,
        website_url: currentWebsite?.url || localStorage.getItem('onboardingWebsite') || undefined,
        message: text,
        history: messages.map(m => ({ role: m.role, content: m.content })),
        website_analysis_data: websiteAnalysisData,
        stream: true
      })
    });

    if (!response.ok) {
      throw new Error('Failed to get response');
    }

    reader = response.body?.getReader() || null;
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let currentContent = '';
    let currentMode: 'unknown' | 'chat' | 'idea' | 'write' = 'unknown';

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        await reader.cancel();
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') {
            setMessages((m) => m.map(msg => 
              msg._id === assistantMessageId 
                ? { ...msg, _streaming: false, _loading: false, _mode: currentMode }
                : msg
            ));
            await reader.cancel();
            break;
          }

          try {
            const parsed = JSON.parse(data);
            if (parsed.content) {
              currentContent += parsed.content;
              
              // Detect mode after we have enough content
              let detectedMode = 'unknown';
              if (currentContent.startsWith('CHAT_MODE:')) {
                detectedMode = 'chat';
              } else if (currentContent.startsWith('IDEA_MODE:')) {
                detectedMode = 'idea';
              } else if (currentContent.startsWith('WRITE_MODE:')) {
                detectedMode = 'write';
              }
              
              // Update mode and content based on detection
              if (detectedMode === 'chat') {
                // Show streaming content for chat mode
                const chatContent = currentContent.replace(/^CHAT_MODE:\s*/, '');
                setMessages((m) => m.map(msg => 
                  msg._id === assistantMessageId 
                    ? { ...msg, content: chatContent, _typing: true, _loading: false, _mode: 'chat' }
                    : msg
                ));
              } else if (detectedMode === 'idea' || detectedMode === 'write') {
                // Keep loading state for JSON modes
                setMessages((m) => m.map(msg => 
                  msg._id === assistantMessageId 
                    ? { ...msg, _loading: true, _mode: detectedMode }
                    : msg
                ));
              } else {
                // Still detecting mode, keep loading
                setMessages((m) => m.map(msg => 
                  msg._id === assistantMessageId 
                    ? { ...msg, _loading: true, _mode: 'unknown' }
                    : msg
                ));
              }
            }
            if (parsed.mode) {
              currentMode = parsed.mode;
              setMessages((m) => m.map(msg => 
                msg._id === assistantMessageId 
                  ? { ...msg, _mode: parsed.mode }
                  : msg
              ));
            }
          } catch (e) {
            // Ignore parsing errors for streaming data
          }
        }
      }
    }

    // Final update to remove loading state and process mode prefixes
    setMessages((m) => m.map(msg => {
      if (msg._id === assistantMessageId) {
        let finalContent = currentContent;
        let finalMode = currentMode;
        
        // Final mode detection and content processing
        if (currentContent.startsWith('CHAT_MODE:')) {
          finalContent = currentContent.replace(/^CHAT_MODE:\s*/, '');
          finalMode = 'chat';
        } else if (currentContent.startsWith('IDEA_MODE:')) {
          finalContent = currentContent.replace(/^IDEA_MODE:\s*/, '');
          finalMode = 'idea';
        } else if (currentContent.startsWith('WRITE_MODE:')) {
          finalContent = currentContent.replace(/^WRITE_MODE:\s*/, '');
          finalMode = 'write';
        }
        
        return { 
          ...msg, 
          content: finalContent,
          _streaming: false, 
          _loading: false, 
          _typing: false, 
          _mode: finalMode 
        };
      }
      return msg;
    }));

  } catch (error) {
    console.error('Error sending message:', error);
    setMessages((m) => [...m, { role: 'assistant', content: 'Sorry, I could not respond just now. Please try again.' }]);
  } finally {
    setSending(false);
    // Ensure reader is properly closed
    if (reader) {
      try {
        await reader.cancel();
      } catch (e) {
        // Ignore cancellation errors
      }
    }
  }
};
